import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import axios from '../lib/axios';
import { formatApiError } from '../utils/formatters';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  
  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    passwordsMatch: false
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);
  
  // Validate password on change
  useEffect(() => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch: password.length > 0 && password === confirmPassword
    });
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password requirements
    if (!passwordValidation.minLength) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!passwordValidation.hasUppercase || !passwordValidation.hasLowercase) {
      toast.error('Password must contain uppercase and lowercase letters');
      return;
    }
    
    if (!passwordValidation.hasNumber) {
      toast.error('Password must contain at least one number');
      return;
    }
    
    if (!passwordValidation.passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      
      // Check if token is expired
      if (detail.toLowerCase().includes('expired') || detail.toLowerCase().includes('invalid')) {
        setTokenExpired(true);
        setError('This password reset link has expired. Please request a new one.');
      } else {
        const message = formatApiError(detail, 'Failed to reset password. Please try again.');
        setError(message);
      }
      toast.error(error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
            {success ? (
              // Success State
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-6 bg-green-500/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Password Reset!</h2>
                <p className="text-muted-foreground mb-8">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3.5 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="go-to-login-btn"
                >
                  Sign In
                </button>
              </div>
            ) : error && !token ? (
              // Invalid Token State
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-500/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Invalid Link</h2>
                <p className="text-muted-foreground mb-8">
                  {error}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3.5 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors"
                >
                  Back to Sign In
                </button>
              </div>
            ) : tokenExpired ? (
              // Expired Token State
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-6 bg-amber-500/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Link Expired</h2>
                <p className="text-muted-foreground mb-4">
                  This password reset link has expired. Reset links are valid for 1 hour.
                </p>
                <p className="text-sm text-muted-foreground mb-8">
                  Please request a new password reset link.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3.5 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors"
                >
                  Request New Link
                </button>
              </div>
            ) : (
              // Reset Form
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-foreground mb-2">Create New Password</h2>
                  <p className="text-muted-foreground">
                    Enter your new password below.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className={`w-full py-3 pl-12 pr-12 bg-secondary border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 outline-none ${
                          password && !passwordValidation.minLength ? 'border-red-500' : 'border-border'
                        }`}
                        required
                        minLength={8}
                        autoFocus
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    
                    {/* Password Requirements */}
                    {password && (
                      <div className="mt-3 p-3 bg-secondary/50 rounded-xl border border-border">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Password requirements:</p>
                        <div className="grid grid-cols-2 gap-1.5 text-xs">
                          <div className={`flex items-center gap-1.5 ${passwordValidation.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {passwordValidation.minLength ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            <span>8+ characters</span>
                          </div>
                          <div className={`flex items-center gap-1.5 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {passwordValidation.hasUppercase ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            <span>Uppercase letter</span>
                          </div>
                          <div className={`flex items-center gap-1.5 ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {passwordValidation.hasLowercase ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            <span>Lowercase letter</span>
                          </div>
                          <div className={`flex items-center gap-1.5 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {passwordValidation.hasNumber ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            <span>Number</span>
                          </div>
                          <div className={`flex items-center gap-1.5 ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {passwordValidation.hasSpecial ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            <span>Special character</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className={`w-full py-3 pl-12 pr-12 bg-secondary border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 outline-none ${
                          confirmPassword && !passwordValidation.passwordsMatch 
                            ? 'border-red-500 focus:border-red-500' 
                            : confirmPassword && passwordValidation.passwordsMatch 
                            ? 'border-green-500 focus:border-green-500' 
                            : 'border-border'
                        }`}
                        required
                        minLength={8}
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {confirmPassword && (
                      <p className={`mt-1.5 text-xs flex items-center gap-1 ${passwordValidation.passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordValidation.passwordsMatch ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Passwords match
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3.5 h-3.5" />
                            Passwords do not match
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="reset-password-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
